import { useMutation, useQuery, useQueryClient } from "react-query";
import apiFetch from "../api";
import { useState, useEffect } from "react";
import { Controller, useForm } from "react-hook-form";
import { useDocument } from "../hooks/useDocument";
import Input from "../Components/Input";


const FeedsInput = ({ feeds = [], value = [], onChange, namePrefix = 'feed' }) => {
  const selectedFeedIds = value.map(v => v.id);
  return (
    <>
      {feeds.map(feed => {
        const inputId = `${namePrefix}-${feed.id}`;
        return (
          <div key={feed.id}>
            <input id={inputId} className="accent-primary-400" type="checkbox" value={feed.id} checked={selectedFeedIds?.includes(feed.id) ?? false} onChange={(e) => {
              if (e.target.checked) {
                onChange([...value, feed]);
              } else {
                onChange(value.filter(v => v.id !== feed.id));
              }
            }} />
            <label className="ml-3" htmlFor={inputId}>{feed.title}</label>
          </div>
        )
      })}
    </>
  )
}


const UserForm = ({ user, feeds }) => {
  const form = useForm({
    defaultValues: {
      email: user.email,
      first_name: user.first_name,
      last_name: user.last_name,
      userprofile: {
        description: user.userprofile?.description,
        feeds: user.userprofile?.feeds || []
      }
    }
  });

  const queryClient = useQueryClient();

  const updateUser = useMutation(async (data) => {
    data.userprofile.feed_ids = data.userprofile.feeds.map(feed => feed.id);
    data.userprofile.feeds = undefined;
    return await apiFetch(`/api/users/me/`, {
      method: 'PATCH',
      body: JSON.stringify(data)
    });
  });

  const onSubmit = async (data) => {
    await updateUser.mutateAsync(data);
    queryClient.invalidateQueries(['users', 'me']);
  }

  return (
    <form className="flex flex-col gap-2" onSubmit={form.handleSubmit(onSubmit)}>
      <div className="form-group">
        <label htmlFor="email">Email</label>
        <Input type="email" {...form.register('email')} />
      </div>
      <div className="form-group">
        <label htmlFor="first_name">First Name</label>
        <Input type="text" {...form.register('first_name')} />
      </div>
      <div className="form-group">
        <label htmlFor="last_name">Last Name</label>
        <Input type="text" {...form.register('last_name')} />
      </div>
      <div className="form-group">
        <label htmlFor="description">Interests</label>
        <Input type="text" {...form.register('userprofile.description')} />
      </div>
      <div className="form-group">
        <label>Feeds</label>
        <Controller
          name="userprofile.feeds"
          control={form.control}
          render={({ field }) => (
            <FeedsInput feeds={feeds} value={field.value} onChange={field.onChange} namePrefix="profile-feed" />
          )}
        />
      </div>
      <div className="text-right">
        <button type="submit" className="btn btn-primary" disabled={updateUser.isLoading}>Save</button>
      </div>
    </form>
  );

};

const PodcastConfigForm = ({ config, feeds, onSave, onDelete, isSaving, isDeleting }) => {
  const form = useForm({
    defaultValues: {
      title: config?.title || "",
      prompt: config?.prompt || "",
      city: config?.city || "",
      feeds: config?.feeds || [],
    },
  });

  useEffect(() => {
    form.reset({
      title: config?.title || "",
      prompt: config?.prompt || "",
      city: config?.city || "",
      feeds: config?.feeds || [],
    });
  }, [config, form]);

  const [copied, setCopied] = useState(false);
  const rssUrl = config?.rss_url;
  const prefix = config ? `config-${config.id}` : 'config-new';

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const handleCopy = async () => {
    if (!rssUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rssUrl);
        setCopied(true);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = rssUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
      }
    } catch (error) {
      console.error("Failed to copy RSS feed URL", error);
    }
  };

  const onSubmit = async (values) => {
    await onSave({
      id: config?.id,
      ...values,
    });
    if (!config) {
      form.reset({
        title: "",
        prompt: "",
        city: "",
        feeds: [],
      });
    }
  };

  return (
    <form className="p-4 border border-gray-200 rounded-lg flex flex-col gap-3 dark:border-gray-700" onSubmit={form.handleSubmit(onSubmit)}>
      <div>
        <h3>{config ? (config.title || "Podcast Configuration") : "Create Podcast Configuration"}</h3>
        <p className="text-sm text-gray-600 dark:text-gray-400">
          {config ? "Update the parameters for this configuration." : "Define a new configuration with its own prompt, feeds, and RSS feed."}
        </p>
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-title`}>Title</label>
        <Input id={`${prefix}-title`} type="text" {...form.register("title", { required: true })} />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-prompt`}>Prompt</label>
        <textarea id={`${prefix}-prompt`} className="w-full border border-gray-300 rounded p-2 min-h-[120px] dark:bg-gray-800 dark:border-gray-700" {...form.register("prompt", { required: true })} />
      </div>
      <div className="form-group">
        <label htmlFor={`${prefix}-city`}>City (for weather)</label>
        <Input id={`${prefix}-city`} type="text" {...form.register("city")} />
      </div>
      <div className="form-group">
        <label>Feeds</label>
        <Controller
          name="feeds"
          control={form.control}
          render={({ field }) => (
            <FeedsInput feeds={feeds} value={field.value || []} onChange={field.onChange} namePrefix={`${prefix}-feed`} />
          )}
        />
      </div>
      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <button type="submit" className="btn-primary" disabled={isSaving}>
          {isSaving ? "Saving..." : config ? "Save Changes" : "Create Configuration"}
        </button>
        {config && onDelete && (
          <button
            type="button"
            className="px-4 py-2 text-sm border border-red-300 text-red-600 rounded dark:border-red-700 dark:text-red-400"
            onClick={() => onDelete(config.id)}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </button>
        )}
      </div>
      {config && rssUrl && (
        <div className="mt-2">
          <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Config RSS Feed</label>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Input type="text" value={rssUrl} readOnly onFocus={(e) => e.target.select()} />
            <button
              type="button"
              className="btn-primary"
              onClick={handleCopy}
              disabled={!rssUrl}
            >
              {copied ? "Copied" : "Copy"}
            </button>
          </div>
        </div>
      )}
    </form>
  );
};

const PodcastConfigsSection = ({ feeds }) => {
  const queryClient = useQueryClient();
  const { data: configsData } = useQuery(["podcast-configs"], async () => {
    return await apiFetch("/api/podcast-configs/");
  }, { enabled: feeds !== undefined });
  const configs = configsData?.results ?? configsData ?? [];
  const [savingKey, setSavingKey] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const saveConfig = useMutation(async ({ id, values }) => {
    const payload = {
      title: values.title,
      prompt: values.prompt,
      city: values.city,
      feed_ids: values.feeds.map(feed => feed.id),
    };
    const path = id ? `/api/podcast-configs/${id}/` : `/api/podcast-configs/`;
    const method = id ? 'PATCH' : 'POST';
    return await apiFetch(path, {
      method,
      body: JSON.stringify(payload),
    });
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries(["podcast-configs"]);
    }
  });

  const deleteConfig = useMutation(async (id) => {
    return await apiFetch(`/api/podcast-configs/${id}/`, {
      method: 'DELETE',
    });
  }, {
    onSuccess: () => {
      queryClient.invalidateQueries(["podcast-configs"]);
    }
  });

  const handleSave = async ({ id, ...values }) => {
    const key = id ?? 'new';
    setSavingKey(key);
    try {
      await saveConfig.mutateAsync({ id, values });
    } finally {
      setSavingKey(null);
    }
  };

  const handleDelete = async (id) => {
    setDeletingId(id);
    try {
      await deleteConfig.mutateAsync(id);
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="mt-10">
      <h2>Podcast Configurations</h2>
      <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
        Create multiple podcast presets with unique prompts, feeds, and weather locations. Each configuration exposes its own RSS feed.
      </p>
      <div className="grid gap-6">
        {configs.map(config => (
          <PodcastConfigForm
            key={config.id}
            config={config}
            feeds={feeds}
            onSave={({ id: _id, ...values }) => handleSave({ id: config.id, ...values })}
            onDelete={handleDelete}
            isSaving={savingKey === config.id && saveConfig.isLoading}
            isDeleting={deletingId === config.id && deleteConfig.isLoading}
          />
        ))}
        <PodcastConfigForm
          key="new-config"
          feeds={feeds}
          onSave={({ id, ...values }) => handleSave({ id: null, ...values })}
          isSaving={savingKey === 'new' && saveConfig.isLoading}
          isDeleting={false}
        />
      </div>
    </div>
  );
};

const Users = () => {
  const [copied, setCopied] = useState(false);
  const { data: userData } = useQuery(['users', 'me'], async () => await apiFetch(`/api/users/me/`));
  useDocument(userData?.first_name ? `${userData.first_name} ${userData.last_name}` : 'Profile');
  const {data: feedsData} = useQuery(["feeds"], async () => {
    return await apiFetch("/api/feeds/");
  }, { keepPreviousData: true });
  const { data: rssData, isLoading: rssLoading } = useQuery(["podcasts", "rss-url"], async () => {
    return await apiFetch("/api/podcasts/rss-url/");
  }, { enabled: !!userData });

  useEffect(() => {
    if (!copied) return;
    const timer = setTimeout(() => setCopied(false), 2000);
    return () => clearTimeout(timer);
  }, [copied]);

  const rssUrl = rssData?.rss_url || "";

  const handleCopy = async () => {
    if (!rssUrl) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(rssUrl);
        setCopied(true);
      } else {
        const textarea = document.createElement("textarea");
        textarea.value = rssUrl;
        textarea.setAttribute("readonly", "");
        textarea.style.position = "absolute";
        textarea.style.left = "-9999px";
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand("copy");
        document.body.removeChild(textarea);
        setCopied(true);
      }
    } catch (error) {
      console.error("Failed to copy RSS feed URL", error);
    }
  };

  const feedOptions = feedsData?.results ?? [];

  return (
    <div className="m-auto max-w-4xl">
      <h1>Profile</h1>
      {userData && feedsData && <UserForm user={userData} feeds={feedOptions} />}
      {rssUrl && (
        <div className="mt-6">
          <h2>Podcast RSS Feed</h2>
          <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
            Copy this link into your podcast provider to publish your daily episodes.
          </p>
          <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
            <Input type="text" value={rssUrl} readOnly onFocus={(e) => e.target.select()} />
            <button
              type="button"
              className="btn-primary"
              onClick={handleCopy}
              disabled={rssLoading}
            >
              {copied ? "Copied" : "Copy Link"}
            </button>
          </div>
          {copied && (
            <span className="text-xs text-green-600 dark:text-green-400 mt-1 block">
              RSS feed URL copied to clipboard.
            </span>
          )}
        </div>
      )}
      {userData && feedsData && (
        <PodcastConfigsSection feeds={feedOptions} />
      )}
    </div>
  )
};

export default Users;
